import React from 'react';
import { Tab } from 'component/page/detail/DetailPageTabs/TabItem';
import { ContentRegistry } from 'component/page/detail/DetailPageModularize/ContentRegistry';
import { getCustomMetaFromContentHandler } from 'component/page/detail/DetailPageModularize/ContentRegistryHelpers';
import { Container, HorizontalScroll } from '@drone-js/core';
import { connect, useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { StickyTopBar } from 'component/common/StickyTopBar/StickyTopBar';
import {
    mapDispatchToProps,
    TabsComponentProps,
    shiftFocusByIndexAndHref,
    mapStateToProps,
} from 'component/page/detail/DetailPageModularize/ContentsHandler/TabsComponent.selectors';
import { RootState } from 'store/store.types';
import { getDocument } from 'core-framework/util/Dom';
import { SIDE_SHEET_ELEMENT_NAME, SIDE_SHEET_HEADER_HEIGHT } from 'core-framework/constant/DetailPageModularConstant';

const TabId = 'activity-nav-tabs';

const TabsComponent = ({ contents, onTabChanged, selectedTab, isScrolling }: TabsComponentProps) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const state = useSelector((rootState: RootState) => rootState);
    const [active, setActive] = React.useState(0);
    const ref = React.useRef<HTMLDivElement>(null);
    const [stickyTop, setStickyTop] = React.useState(0);
    const userChangeTabRef = React.useRef(false);

    React.useEffect(() => {
        const doc = getDocument();
        const sideSheet = doc.querySelector(`[data-element-name="${SIDE_SHEET_ELEMENT_NAME}"]`);
        const isUnderSideSheet = sideSheet?.contains(ref.current) ?? false;
        setStickyTop(isUnderSideSheet ? SIDE_SHEET_HEADER_HEIGHT : 0);
    }, []);

    const validContents = React.useMemo(() => {
        return contents.filter((content) => {
            const contentValidation = ContentRegistry[content.content].contentValidation;
            return !contentValidation || contentValidation(state);
        });
    }, [contents, state]);

    const getIndexFromSelectedTab = React.useCallback(() => {
        const index = validContents.findIndex((x) => ContentRegistry[x.content].href === selectedTab);
        return index === -1 ? 0 : index;
    }, [selectedTab, validContents]);

    React.useEffect(() => {
        if (!userChangeTabRef.current && !isScrolling) {
            setActive(getIndexFromSelectedTab());
        }
        userChangeTabRef.current = false;
    }, [selectedTab, getIndexFromSelectedTab, isScrolling]);

    const onChanged = (index: number) => {
        userChangeTabRef.current = true;
        const content = validContents[index];
        const { titleCms, href, dynamicTitle } = getCustomMetaFromContentHandler(content, state);
        const label = dynamicTitle || cms[titleCms];
        setActive(index);
        onTabChanged({
            title: label,
            href,
        });
        shiftFocusByIndexAndHref(href);
    };

    const tabItems = validContents.map((x, i) => {
        const { titleCms, href, dynamicTitle } = getCustomMetaFromContentHandler(x, state);
        const label = dynamicTitle || cms[titleCms];

        return (
            <Tab
                label={label}
                id={`tab-button-${href}`}
                key={`tab-button-${href}`}
                aria-label={label}
                onClick={() => onChanged(i)}
                isActive={active === i}
                width="content-fit"
                size="s"
                aria-current={active === i ? 'true' : 'false'}
            />
        );
    });

    if (validContents.length === 0) {
        return null;
    }

    return (
        <StickyTopBar elementName="activities-nav-tabs-container" zIndex={3} noShadow={true} fullWidth={true} noBorder={true} stickyTop={stickyTop}>
            <Container width="100%" ref={ref}>
                <HorizontalScroll
                    id={TabId}
                    aria-label={cms[342164]}
                    data-testid={TabId}
                    nextLabel={cms[130706]}
                    previousLabel={cms[130765]}
                    width="fit-content"
                    minWidth="100%"
                    whiteSpace="nowrap"
                    onActiveChange={onChanged}
                    active={active}
                    borderWidth={['0', '0', '1', '0']}
                    borderStyle="solid"
                    borderColor="base.default"
                    behavior="item"
                    restrictFocus={true}
                    role="navigation"
                >
                    {tabItems}
                </HorizontalScroll>
            </Container>
        </StickyTopBar>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(TabsComponent);

TabsComponent.displayName = 'TabsComponent';
